import { useState, useEffect, useRef, useCallback } from "react";
import { IconUser, IconCheck, IconAlertCircle, IconLoader } from "@tabler/icons-react";
import { validateRodCislo, autoFormatRodCislo, validateRodCisloForChild } from "../utils/rodCisloValidation";
import CityAutocomplete from "./CityAutocomplete";

interface SchoolYear {
    id: string;
    name: string;
    order: number;
    type: string;
}

interface PersonData {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address: string;
    houseNumber: string;
    city: string;
    psc: string;
    district?: string;
    rodCislo?: string;
    gender?: 'MALE' | 'FEMALE' | 'BOTH';
    schoolyearId?: string;
    isBernolakova?: boolean;
    schoolName?: string;
    type: 'PARENT' | 'ADULT' | 'KID';
}

interface PersonFormProps {
    /** Initial data for editing (if undefined, creates new person) */
    initialData?: Partial<PersonData>;
    /** Person type for new person creation */
    personType?: 'PARENT' | 'ADULT' | 'KID';
    /** Current user's email for default values */
    userEmail?: string;
    /** Existing person data for address defaults */
    existingPersonData?: {
        address?: string;
        houseNumber?: string;
        city?: string;
        psc?: string;
        phone?: string;
    };
    /** Custom submit handler - if not provided, uses default API calls */
    onSubmit?: (data: PersonData) => Promise<void>;
    /** Enable autosave (debounced) on field change */
    autosave?: boolean;
    /** Success callback */
    onSuccess?: (personId?: string) => void;
    /** Cancel callback */
    onCancel?: () => void;
    /** Whether form is in modal/embedded mode (affects styling) */
    isEmbedded?: boolean;
    /** Custom title override */
    title?: string;
    /** Whether to show delete button (for edit mode) */
    showDelete?: boolean;
    /** Delete handler */
    onDelete?: () => void;

    readOnly?: boolean;
}

const PersonForm = ({
    initialData,
    personType = 'PARENT',
    userEmail,
    existingPersonData,
    onSubmit,
    autosave = false,
    onSuccess,
    onCancel,
    isEmbedded = false,
    title,
    showDelete = false,
    onDelete,
    readOnly = false
}: PersonFormProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
    const [isBernolakova, setIsBernolakova] = useState(initialData?.isBernolakova || false);
    const [rodCisloError, setRodCisloError] = useState<string | null>(null);
    const [showBernolakovaConfirmation, setShowBernolakovaConfirmation] = useState(false);

    // Determine if this is edit mode
    const isEditMode = !!initialData?.id;
    // Determine the actual person type
    const actualType = initialData?.type || personType;

    // Form data state
    const [formData, setFormData] = useState({
        firstName: initialData?.firstName || '',
        lastName: initialData?.lastName || '',
        phone: initialData?.phone || existingPersonData?.phone || '',
        address: initialData?.address || existingPersonData?.address || '',
        houseNumber: initialData?.houseNumber || existingPersonData?.houseNumber || '',
        city: initialData?.city || existingPersonData?.city || '',
        psc: initialData?.psc || existingPersonData?.psc || '',
        district: (initialData as any)?.district || '',
        rodCislo: initialData?.rodCislo || '',
        gender: initialData?.gender || '',
        schoolyearId: initialData?.schoolyearId || '',
        schoolName: initialData?.schoolName || '',
        email: initialData?.email || ((initialData?.type === 'ADULT' || initialData?.type === 'PARENT') ? (userEmail || '') : '')
    });

    // UX timing: ensure spinner is visible briefly and success is shown briefly
    const MIN_SPINNER_MS = 700; // minimum spinner time to make it visible
    const SUCCESS_SHOW_MS = 5000; // how long to show the check icon

    // Autosave debounce logic (moved below defaultSubmitHandler to avoid forward reference)

    // Fetch school years when component mounts
    useEffect(() => {
        const fetchSchoolYears = async () => {
            try {
                const response = await fetch('/api/vseob/rocnik');
                if (response.ok) {
                    const data = await response.json();
                    setSchoolYears(data.data ? data.data.sort((a: SchoolYear, b: SchoolYear) => (a.order || 0) - (b.order || 0)) : data);
                }
            } catch (error) {
                console.error('Failed to fetch school years:', error);
            }
        };

        fetchSchoolYears();
    }, []);

    const isSchoolYearBernolakovaEligible = useCallback((schoolyearId?: string) => {
        if (!schoolyearId) return false;

        const selectedYear = schoolYears.find((year) => year.id === schoolyearId);
        return selectedYear?.name?.includes('ZŠ') ?? false;
    }, [schoolYears]);

    useEffect(() => {
        if (actualType !== 'KID') return;
        if (!isBernolakova) return;
        // An unresolved school year does not establish ineligibility.
        if (!schoolYears.some(year => year.id === formData.schoolyearId)) return;
        if (isSchoolYearBernolakovaEligible(formData.schoolyearId)) return;

        // Keep Bernolakova selected only for school years that include "ZŠ".
        setIsBernolakova(false);
        setFormData(prev => ({
            ...prev,
            schoolName: ''
        }));
    }, [actualType, formData.schoolyearId, isBernolakova, isSchoolYearBernolakovaEligible, schoolYears]);

    const isBernolakovaSelectable = actualType === 'KID' && isSchoolYearBernolakovaEligible(formData.schoolyearId);

    const toggleBernolakova = () => {
        if (!isBernolakovaSelectable) return;

        setIsBernolakova(!isBernolakova);
        setFormData(prev => ({
            ...prev,
            schoolName: !isBernolakova ? 'ZŠ sv. Cyrila a Metoda, Bernolákova 18' : ''
        }));
    };

    const setBernolakovaConfirmation = (attendsBernolakova: boolean) => {
        setIsBernolakova(attendsBernolakova);
        setFormData(prev => ({
            ...prev,
            schoolName: attendsBernolakova ? 'ZŠ sv. Cyrila a Metoda, Bernolákova 18' : ''
        }));
        setShowBernolakovaConfirmation(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name === 'rodCislo') {
            // Auto-format rodCislo as user types
            const formattedValue = autoFormatRodCislo(value);
            setFormData(prev => ({
                ...prev,
                [name]: formattedValue
            }));

            // Validate rodCislo if it's not empty
            if (formattedValue.trim()) {
                let validation;

                // Use child-specific validation for KID type
                if (actualType === 'KID') {
                    validation = validateRodCisloForChild(formattedValue);
                } else {
                    validation = validateRodCislo(formattedValue);
                }

                setRodCisloError(validation.isValid ? null : (validation.error || 'Neplatný formát rodného čísla'));
            } else {
                setRodCisloError(null);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));

            if (name === 'schoolyearId' && actualType === 'KID') {
                if (isSchoolYearBernolakovaEligible(value)) {
                    setShowBernolakovaConfirmation(true);
                } else {
                    setIsBernolakova(false);
                    setFormData(prev => ({
                        ...prev,
                        schoolName: ''
                    }));
                }
            }
        }
    };

    const getTitle = () => {
        if (title) return title;

        if (isEditMode) {
            switch (actualType) {
                case 'PARENT':
                    return 'Upraviť údaje zákonného zástupcu';
                case 'ADULT':
                    return 'Upraviť údaje dospelého študenta';
                default:
                    return 'Upraviť údaje dieťaťa';
            }
        } else {
            switch (actualType) {
                case 'PARENT':
                    return 'Údaje zákonného zástupcu';
                case 'ADULT':
                    return 'Údaje dospelého študenta';
                default:
                    return 'Údaje dieťaťa';
            }
        }
    };

    const defaultSubmitHandler = useCallback(async (data: PersonData) => {
        if (isEditMode) {
            // Update existing person
            const response = await fetch(`/api/user/person/${initialData?.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update person');
            }

            return response.json();
        } else {
            // Create new person
            const response = await fetch('/api/user/person', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create person');
            }

            return response.json();
        }
    }, [isEditMode, initialData]);

    // guard to avoid concurrent/periodic autosave loops
    const savingRef = useRef(false);
    // keep latest onSubmit in a ref to avoid effect dependency on function identity
    const onSubmitRef = useRef(onSubmit);
    useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

    // remember last saved serialized form to avoid saving unchanged data
    const lastSavedRef = useRef<string | null>(null);

    // Autosave debounce logic
    useEffect(() => {
        if (!autosave) return;
        // Only autosave for existing person edits
        if (!isEditMode) return;

        // Debounce autosave
        const handler = setTimeout(() => {
            // don't start another autosave while one is running
            if (savingRef.current) return;

            const personData: PersonData = {
                ...formData,
                email: formData.email || '',
                isBernolakova: actualType === 'KID' ? isBernolakova : false,
                type: actualType,
                gender: formData.gender as 'MALE' | 'FEMALE' | 'BOTH' | undefined,
                ...(isEditMode && { id: initialData?.id })
            };

            (async () => {
                // avoid saving if nothing changed
                const serialized = JSON.stringify(personData);
                if (lastSavedRef.current === serialized) {
                    return;
                }

                savingRef.current = true;
                setError(null);
                setIsLoading(true);
                const start = Date.now();
                try {
                    const submitHandler = onSubmitRef.current || defaultSubmitHandler;
                    await submitHandler(personData);

                    // ensure spinner is visible for at least MIN_SPINNER_MS
                    const elapsed = Date.now() - start;
                    const wait = Math.max(0, MIN_SPINNER_MS - elapsed);
                    setTimeout(() => {
                        setIsLoading(false);
                        setSuccess(true);
                        // show success briefly
                        setTimeout(() => setSuccess(false), SUCCESS_SHOW_MS);
                        savingRef.current = false;
                        lastSavedRef.current = serialized;
                    }, wait);
                } catch (err) {
                    setIsLoading(false);
                    setError(err instanceof Error ? err.message : 'An error occurred');
                    savingRef.current = false;
                }
            })();
        }, 800);

        return () => clearTimeout(handler);
    }, [formData, autosave, isEditMode, actualType, initialData, isBernolakova, onSubmit, defaultSubmitHandler]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        // Validate rodCislo for non-PARENT types before submission
        if (actualType !== 'PARENT' && formData.rodCislo) {
            let validation;

            // Use child-specific validation for KID type
            if (actualType === 'KID') {
                validation = validateRodCisloForChild(formData.rodCislo);
            } else {
                validation = validateRodCislo(formData.rodCislo);
            }

            if (!validation.isValid) {
                setError(validation.error || 'Neplatný formát rodného čísla');
                setIsLoading(false);
                return;
            }
        }

        const personData: PersonData = {
            ...formData,
            // prefer the editable form email first; fallback to userEmail
            email: formData.email || userEmail || '',
            isBernolakova: actualType === 'KID' ? isBernolakova : false,
            type: actualType,
            gender: formData.gender as 'MALE' | 'FEMALE' | 'BOTH' | undefined,
            ...(isEditMode && { id: initialData?.id })
        };

        try {
            const submitHandler = onSubmit || defaultSubmitHandler;
            const result = await submitHandler(personData);

            setSuccess(true);


            if (onSuccess) {
                onSuccess(result?.id || initialData?.id);
            } else if (!isEmbedded) {
                // Default redirect behavior for standalone form
                setTimeout(() => {
                    window.location.href = '/main';
                }, 1000);
            }

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const formContent = (
        <>
            {showBernolakovaConfirmation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" role="dialog" aria-modal="true" aria-labelledby="bernolakova-confirmation-title">
                    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                        <h2 id="bernolakova-confirmation-title" className="text-lg font-semibold text-gray-900">
                            Navštevuje vaše dieťa ZŠ sv. Cyrila a Metoda, Bernolákova 18?
                        </h2>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setBernolakovaConfirmation(false)}
                                className="rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Nie
                            </button>
                            <button
                                type="button"
                                onClick={() => setBernolakovaConfirmation(true)}
                                className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:opacity-90"
                            >
                                Áno
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Diacritics notice - shown when editing person data */}
            {!readOnly && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800 flex items-center">
                        <IconAlertCircle className="inline h-4 w-4 mr-2" />
                        <p className="font-semibold">Všetky údaje zadávajte s diakritikou!!</p>
                    </p>
                </div>
            )}

            {(autosave && !readOnly) &&
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800 flex items-center">
                        {isLoading ? (<IconLoader className="inline h-4 w-4 mr-1 animate-spin" />) : (<IconCheck className="inline h-4 w-4 mr-1" />)}
                        Sekcia osoby sa ukladá automaticky.

                    </p>
                </div>
            }
            {
                error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
                        <IconAlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="text-sm font-medium text-red-800">Chyba</h3>
                            <p className="text-sm text-red-700 mt-1">{error}</p>
                        </div>
                    </div>
                )
            }

            {
                (success && !autosave) && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-start">
                        <IconCheck className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="text-sm font-medium text-green-800">Úspech</h3>
                            <p className="text-sm text-green-700 mt-1">
                                {isEditMode ? 'Údaje boli úspešne aktualizované!' : 'Údaje boli úspešne uložené!'}
                            </p>
                        </div>
                    </div>
                )
            }

            <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="firstName" className="form-label">
                            Meno *
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="lastName" className="form-label">
                            Priezvisko *
                        </label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                        />
                    </div>
                </div>

                {(actualType === 'KID' || actualType === 'ADULT') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="rodCislo" className="form-label">
                                Rodné číslo *
                            </label>
                            <input
                                type="text"
                                id="rodCislo"
                                name="rodCislo"
                                value={formData.rodCislo}
                                onChange={handleInputChange}
                                placeholder="XXXXXX/XXXX"
                                className={`form-input ${rodCisloError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                required={true}
                            />
                            {rodCisloError && (
                                <p className="mt-1 text-sm text-red-600">
                                    {rodCisloError}
                                </p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="gender" className="form-label">
                                Pohlavie *
                            </label>
                            <select
                                id="gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                className="form-input"
                                required={true}
                            >
                                <option value="">Vyberte pohlavie</option>
                                <option value="MALE">Muž</option>
                                <option value="FEMALE">Žena</option>
                            </select>
                        </div>
                    </div>
                )}

                {(actualType === 'PARENT') && (
                    <div className="grid grid-cols-1  gap-6">

                        <div>
                            <label htmlFor="gender" className="form-label">
                                Vzťah k dieťaťu *
                            </label>
                            <select
                                id="gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                className="form-input"
                                required={true}
                            >
                                <option value="">Vyberte</option>
                                <option value="FEMALE">Matka / Zákonný zástupca</option>
                                <option value="MALE">Otec / Zákonný zástupca</option>
                            </select>
                        </div>
                    </div>
                )}



                {(actualType === 'KID' || actualType === 'ADULT') && (
                    <div>
                        <label htmlFor="schoolyearId" className="form-label">
                            Ročník *
                        </label>
                        <select
                            id="schoolyearId"
                            name="schoolyearId"
                            value={formData.schoolyearId}
                            onChange={handleInputChange}
                            className="form-input"
                        >
                            <option value="">Vyberte ročník</option>
                            {schoolYears.filter(year => actualType === 'ADULT' ? (year.type === "HIGHSCHOOL" || year.type === "UNIVERSITY") : true).map((year) => (
                                <option key={year.id} value={year.id}>
                                    {year.name}
                                </option>
                            ))}
                        </select>
                        <label htmlFor="schoolyearId2" className="form-label">Pri návšteve päť alebo osemročného gymnázia zvoľte ročník zodpovedajúci klasickej forme štúdia</label>
                    </div>
                )}




                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                        <label htmlFor="address" className="form-label">
                            Ulica *
                        </label>
                        <input
                            type="text"
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Názov ulice"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="houseNumber" className="form-label">
                            Číslo domu *
                        </label>
                        <input
                            type="text"
                            id="houseNumber"
                            name="houseNumber"
                            value={formData.houseNumber}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="123"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="city" className="form-label">
                            Mesto *
                        </label>
                        <CityAutocomplete
                            value={formData.city}
                            onChange={(city) => setFormData(prev => ({ ...prev, city }))}
                            onCitySelect={(city, psc, district) => setFormData(prev => ({ ...prev, city, psc, district }))}
                            disabled={readOnly}
                            readOnly={readOnly}
                            required={true}
                        />
                    </div>
                    <div>
                        <label htmlFor="psc" className="form-label">
                            PSČ *
                        </label>
                        <input
                            type="text"
                            id="psc"
                            name="psc"
                            value={formData.psc}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="XXX XX"
                            required
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="email" className="form-label">
                            E-mail
                        </label>
                        <input
                            type="email"
                            id="emailx"
                            name="email"
                            value={actualType !== 'KID' ? (userEmail || formData.email || '') : formData.email}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={actualType !== 'KID'}
                            placeholder={actualType === 'KID' ? 'zadajte@email.com' : ''}
                            readOnly={actualType !== 'KID'}
                        />
                    </div>

                    <div>
                        <label htmlFor="phone" className="form-label">
                            Telefónne číslo
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="+421 XXX XXX XXX"
                        />
                    </div>
                </div>

                {actualType === 'KID' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="schoolName" className="form-label">
                                Názov školy (orientačný) *
                            </label>
                            <input
                                type="text"
                                id="schoolName"
                                name="schoolName"
                                value={isBernolakova ? 'ZŠ sv. Cyrila a Metoda, Bernolákova 18' : formData.schoolName}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="Zadajte názov školy"
                                required={!isBernolakova}
                                disabled={isBernolakova}
                            />
                        </div>
                        <div className="flex items-center md:pt-6" title="Túto možnosť je možné označiť iba ak je zvolený ročník ZŠ">
                            <div
                                onClick={toggleBernolakova}
                                className={`flex justify-center items-center size-8 border border-gray-300 rounded ${isBernolakova ? 'bg-primary' : ''} ${isBernolakovaSelectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                            >
                                {isBernolakova ? <IconCheck color="white" /> : null}
                            </div>
                            <label
                                className={`ml-2 text-sm text-gray-700 ${isBernolakovaSelectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                onClick={toggleBernolakova}>
                                Navštevuje ZŠ sv. Cyrila a Metoda, Bernolákova 18
                            </label>
                        </div>



                    </div>
                )}

                {actualType === 'ADULT' && (
                    <div>
                        <label htmlFor="schoolName" className="form-label">
                            Názov školy/organizácie *
                        </label>
                        <input
                            type="text"
                            id="schoolName"
                            name="schoolName"
                            value={formData.schoolName}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Zadajte názov školy alebo organizácie"
                        />
                    </div>
                )}



                <div className={` ${!autosave ? 'pt-6border-t border-gray-200' : ''}  flex gap-4`}>
                    {!autosave && <button
                        type="submit"
                        disabled={isLoading}
                        className={`flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center space-x-2 transition-colors ${isLoading ? '' : ''}`}
                    >
                        {isLoading && <IconLoader className="h-4 w-4 animate-spin" />}
                        <span>{isLoading ? (isEditMode ? 'Aktualizujem...' : 'Ukladám...') : (isEditMode ? 'Aktualizovať údaje' : 'Uložiť údaje')}</span>
                    </button>}

                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                        >
                            Zrušiť
                        </button>
                    )}

                    {showDelete && onDelete && (
                        <button
                            type="button"
                            onClick={onDelete}
                            className="px-6 py-3 border border-red-300 rounded-md text-red-600 hover:bg-red-50 font-medium transition-colors"
                        >
                            Odstrániť
                        </button>
                    )}
                </div>
            </form>
        </>
    );

    if (isEmbedded) {
        return <div className="space-y-6">{formContent}</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center">
                <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 mr-4">
                    <IconUser className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">{getTitle()}</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Vyplňte všetky povinné údaje pre dokončenie {isEditMode ? 'aktualizácie' : 'registrácie'}
                    </p>
                </div>
            </div>
            {formContent}
        </div>
    );
};

export default PersonForm;
